import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { ButtonItem, LinkItem } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';

import {
	getMaximumItemLimit,
	getMaximumNestingLevelLimit,
	type LinksMenuExtensionActions,
} from '../../../../../../controllers/use-links-menu-parameters-and-actions';
import { ITEM, type ItemPositionalProps, type ItemType } from '../../../../../../types';
import { NewItemSelection } from '../../../new-item-button/new-item-selection';

import { DeleteItemButton } from './delete-item-button';
import { messages } from './messages';

type LinksMenuItemDropdownMenuProps = ItemPositionalProps & {
	extensionActions: LinksMenuExtensionActions;
	item: ItemType;
	numberItems: number;
	parentId?: string;
	setIsConfiguring: (isConfiguring: boolean) => void;
	setIsPopupOpen: (isPopupOpen: boolean) => void;
	setNewItemType: (newItemType: ITEM | undefined) => void;
};

export const LinksMenuItemDropdownMenu: React.FC<LinksMenuItemDropdownMenuProps> = ({
	extensionActions,
	index,
	isFirst,
	isLast,
	item,
	parentId,
	level,
	numberItems,
	setIsPopupOpen,
	setIsConfiguring,
	setNewItemType,
}) => {
	const { moveItem } = extensionActions;
	const [isNestedPopupOpen, setIsNestedPopupOpen] = useState(false);

	const handleOpenLinkClick = () => {
		setIsPopupOpen(false);
	};

	const handleEditPropertiesClick = () => {
		setIsConfiguring(true);
		setNewItemType(undefined);
	};

	const handleMoveUpClick = () => {
		moveItem(item, index - 1, parentId);
		setIsPopupOpen(false);
	};

	const handleMoveDownClick = () => {
		moveItem(item, index + 1, parentId);
		setIsPopupOpen(false);
	};

	const handleSubItemPopupClose = () => setIsNestedPopupOpen(false);

	const reachedMaximumNestingLevel = level >= getMaximumNestingLevelLimit();
	const reachedMaximumItems = numberItems >= getMaximumItemLimit();

	return (
		<>
			{item.type === ITEM.LINK && (
				<LinkItem
					href={item.link}
					target="_blank"
					onClick={handleOpenLinkClick}
					isDisabled={!item.link}
				>
					<FormattedMessage {...messages.openLinkButtonText} />
				</LinkItem>
			)}
			<ButtonItem onClick={handleEditPropertiesClick}>
				<FormattedMessage {...messages.editPropertiesButtonText} />
			</ButtonItem>
			{item.type === ITEM.MENU && (
				<Tooltip
					content={<FormattedMessage {...messages.itemCreationDisabledTooltipText} />}
					canAppear={() => reachedMaximumItems}
				>
					<Popup
						isOpen={isNestedPopupOpen}
						placement="auto"
						onClose={handleSubItemPopupClose}
						content={() => (
							<NewItemSelection
								isAddMenuSubItemDisabled={reachedMaximumNestingLevel}
								setIsConfiguring={setIsConfiguring}
								setNewItemType={setNewItemType}
							/>
						)}
						trigger={(triggerProps) => (
							<ButtonItem
								{...triggerProps}
								isDisabled={reachedMaximumItems}
								isSelected={isNestedPopupOpen}
								onClick={() => setIsNestedPopupOpen(true)}
								iconAfter={<ArrowRightIcon label="Add sub item" />}
							>
								<FormattedMessage {...messages.addSubItemButtonText} />
							</ButtonItem>
						)}
					/>
				</Tooltip>
			)}
			<ButtonItem isDisabled={isFirst} onClick={handleMoveUpClick}>
				<FormattedMessage {...messages.moveItemUpText} />
			</ButtonItem>
			<ButtonItem isDisabled={isLast} onClick={handleMoveDownClick}>
				<FormattedMessage {...messages.moveItemDownText} />
			</ButtonItem>
			<DeleteItemButton item={item} extensionActions={extensionActions} />
		</>
	);
};
